import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import type { PriceBookSku, ServiceDefinition } from '../../lib/pricebook/pricebook';
import { getServiceBySlug } from '../../lib/pricebook/pricebook';
import MobileSnapshotFab from '../../components/funnel/MobileSnapshotFab';
import ShareCheckoutLinkModal from '../../components/funnel/ShareCheckoutLinkModal';
import { useFunnelCart } from '../../context/FunnelCartProvider';
import { usePricebookCatalogSync } from '../../hooks/usePricebookCatalogSync';
import {
  buildSharedServiceConfigUrl,
  parseSharedServiceConfigToken,
} from '../../lib/funnel/shareServiceConfig';
import { gradientPillCss } from '../../styles/gradientPill';
import FunnelShell from './FunnelShell';

const HOW_IT_WORKS = Object.freeze([
  'Submit unlimited requests into one shared ticket backlog.',
  'CyBard triages each request within 24-48 business hours.',
  'One ticket is actively in progress at a time for predictable throughput.',
  'Communication stays async in the ticketing workflow by design.',
]);

const INCLUDED_BY_DEFAULT = Object.freeze([
  'Unlimited backlog intake',
  'One active in-progress lane at a time',
  'Async execution via tickets',
  'Monthly subscription model with cancel-anytime terms',
]);

const NOT_INCLUDED = Object.freeze([
  'No project manager',
  'No account manager',
  'No custom project-based statement-of-work delivery',
  'No dedicated live-meeting cadence by default',
]);

const BEST_FIT = Object.freeze([
  'You need ongoing design/asset output on retainer.',
  'You are comfortable with async ticket-based collaboration.',
  'You want predictable queue flow rather than project sprint staffing.',
]);

const HOW_IT_WORKS_X_OFFSETS = Object.freeze([-34, 46, -20, 32]);
const HOW_IT_WORKS_Y_OFFSETS = Object.freeze([-6, 10, -10, 8]);
const HOW_IT_WORKS_TILTS = Object.freeze([-2.1, 1.4, 2.7, 12.2]);
const HOW_IT_WORKS_CONNECTOR_TILTS = Object.freeze([30, -25, 28]);
const HOW_IT_WORKS_CONNECTOR_OFFSETS = Object.freeze([300, 246, 276]);
const HOW_IT_WORKS_CONNECTOR_SCALES = Object.freeze([1, 0.95, 1.05]);
const HOW_IT_WORKS_CONNECTOR_TOPS = Object.freeze([105, 100, 130]);
const HOW_IT_WORKS_CONNECTOR_MAX_WIDTHS = Object.freeze([330, 310, 301]);

const cardSway = keyframes`
  0%   { transform: rotate(-12deg) rotateY(6deg) translateY(-4px); }
  50%  { transform: rotate(-9deg) rotateY(-6deg) translateY(4px); }
  100% { transform: rotate(-6deg) rotateY(6deg) translateY(-4px); }
`;

const cardBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const profileEnterFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(26px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const profileExitToLeft = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-26px);
  }
`;

const blimpGlide = keyframes`
  0% {
    transform: translate3d(0, 0, 0) rotate(-2deg);
  }
  20% {
    transform: translate3d(12px, -10px, 0) rotate(1deg);
  }
  45% {
    transform: translate3d(22px, 4px, 0) rotate(2.4deg);
  }
  70% {
    transform: translate3d(8px, 14px, 0) rotate(0.8deg);
  }
  100% {
    transform: translate3d(0, 0, 0) rotate(-2deg);
  }
`;

const PROFILE_ITEM_TRANSITION_MS = 320;

function haveSameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
}

const playfulTitleTilt = css`
  font-family: var(--font-hand);
  display: inline-block;
  transform: rotate(-1.5deg);
  transform-origin: left center;
  letter-spacing: 0.01em;
`;

const Layout = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(0, 1fr) 280px;

  @media (max-width: 1080px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Main = styled.section`
  display: grid;
  gap: 1rem;
`;

const Hero = styled.header`
  // border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 18px;
  // background: ${({ theme }) => theme.colors.gray050};
  padding: 1rem;
  color: #e0a15e;
  display: grid;
  gap: 0.65rem;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.2rem, 6vw, 4.4rem);
  line-height: 0.95;
  ${playfulTitleTilt};
`;

const SectionBlock = styled.section`
  border: none;
  border-radius: 18px;
  background: transparent;
  padding: 1rem;
  display: grid;
  gap: 5rem;
  align-content: start;
  min-height: calc(100svh - 96px);

  @media (max-width: 1080px) {
    min-height: auto;
  }
`;

const FirstSectionBlock = styled(SectionBlock)`
  margin-top: clamp(1.75rem, 5vh, 4rem);
  min-height: auto;
  gap: clamp(1.25rem, 2.8vw, 2rem);

  @media (max-width: 1080px) {
    margin-top: clamp(0.85rem, 2.8vh, 1.6rem);
  }
`;

const CompactSectionBlock = styled(SectionBlock)`
  min-height: auto;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.35rem, 2.15vw, 1.85rem);
  line-height: 1.08;
  ${playfulTitleTilt};
`;

const SectionLead = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray700};
`;

const FirstSectionTitle = styled(SectionTitle)`
  display: block;
  line-height: 0.95;
`;

const FirstSectionLead = styled(SectionLead)`
  line-height: 1.35;
`;

const FirstSectionHeader = styled.div`
  display: grid;
  gap: 0.12rem;
  align-content: start;
`;

const FirstSectionShowcase = styled.div`
  width: min(560px, 100%);
  margin-inline: auto;
  display: grid;
  gap: 0.3rem;
`;

const PlanShowcase = styled.div`
  width: 100%;
  margin-inline: auto;
  margin-top: 0.1rem;
  padding-top: 20px;

  @media (max-width: 900px) {
    padding-top: 14px;
  }
`;

const JoinCard = styled.div`
  background: #fff url('/images/cardboardTexture.jpg') center/cover no-repeat;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  min-height: 380px;
  padding: 2.4rem 1.9rem 2.3rem;
  position: relative;
  box-shadow:
    0 14px 28px rgba(0, 0, 0, 0.12),
    0 34px 54px rgba(0, 0, 0, 0.2);
  isolation: isolate;

  @media (max-width: 900px) {
    min-height: 300px;
    padding: 1.45rem 1.2rem 1.2rem;
  }
`;

const GiftCardWrapper = styled.div`
  position: absolute;
  top: -8px;
  left: -36px;
  width: 380px;
  height: 220px;
  perspective: 1000px;
  z-index: 3;
  pointer-events: none;

  @media (max-width: 900px) {
    top: -4px;
    left: -8px;
    width: 230px;
    height: 132px;
  }
`;

const GiftCard = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  will-change: transform, box-shadow;
  background: url('/images/rubberDuck.png') center/cover no-repeat;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
  animation: ${cardSway} 9s ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const JoinContent = styled.div`
  display: flex;
  flex-direction: column;
  width: min(320px, 100%);
  color: #17212f;
`;

const JoinButton = styled.button`
  --accent: #ffb800;
  width: fit-content;
  background: #000;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 1.15rem 0.5rem 1rem;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1rem;
`;

const BlinkDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ffd600;
  animation: ${cardBlink} 3s ease-in-out infinite;
`;

const JoinHeading = styled.h3`
  margin: 0;
  line-height: 1.06;
  font-size: clamp(2rem, 4.8vw, 2.6rem);
  color: #1f2937;
`;

const JoinSubtext = styled.p`
  margin: 0.55rem 0 0;
  color: #1f2937;
  line-height: 1.45;
  font-weight: 700;
`;

const RulesMiniGrid = styled.div`
  display: grid;
  gap: 3.75rem;
  grid-template-columns: minmax(0, 1fr);
  justify-content: start;
`;

const MiniCard = styled.article`
  border: none;
  border-radius: 0;
  padding: 0.75rem;
  display: grid;
  gap: 0.5rem;
  background: transparent;
`;

const MiniCardIcon = styled.img`
  width: clamp(72px, 9vw, 108px);
  height: auto;
  object-fit: contain;
  margin-bottom: 0.35rem;
`;

const RuleList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.52rem;
`;

const RuleContentRow = styled.div<{ $imageSide: 'left' | 'right' }>`
  display: grid;
  gap: 2rem;
  align-items: stretch;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-height: clamp(220px, 26vw, 300px);

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    min-height: auto;
  }
`;

const RuleTextColumn = styled.div<{ $align?: 'left' | 'right' }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.38rem;
  min-height: 100%;
  text-align: ${({ $align }) => ($align === 'right' ? 'right' : 'left')};
  align-items: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'stretch')};
`;

const RuleImageColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;

  @media (max-width: 900px) {
    min-height: auto;
  }
`;

const RuleMascot = styled.img`
  width: 100%;
  height: auto;
  max-height: clamp(220px, 26vw, 300px);
  object-fit: cover;
  display: block;
  box-shadow: 0 8px 16px rgba(5, 10, 26, 0.22);
  border-radius: 10px;
`;

const RuleItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.42rem;
  align-items: start;
  color: ${({ theme }) => theme.colors.gray700};
  line-height: 1.33;
`;

const RuleGlyph = styled.span<{ $kind: 'include' | 'exclude' }>`
  font-size: 0.9rem;
  font-weight: 900;
  line-height: 1.2;
  margin-top: 0.05rem;
  color: ${({ $kind }) => ($kind === 'include' ? '#e0a15e' : '#ef4444')};
`;

const HowItWorksLayout = styled.div`
  display: grid;
  gap: 1rem;
  width: min(860px, 100%);
  margin-inline: auto;
`;

const HowItWorksColumnTitle = styled.h3`
  margin: 0;
  font-size: 1.02rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.gray900};
`;

const HowItWorksSectionTitle = styled(SectionTitle)`
  transform: rotate(-2.8deg);
`;

const HowItWorksFlowTitle = styled(HowItWorksColumnTitle)`
  width: 100%;
  text-align: center;
  color: #e0a15e;
  font-size: clamp(1.25rem, 2vw, 1.45rem);
`;

const HowItWorksFlow = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.62rem;
`;

const HowItWorksStep = styled.li<{
  $align: 'left' | 'right';
  $offsetX: number;
  $offsetY: number;
  $tilt: number;
  $hasConnector: boolean;
}>`
  position: relative;
  display: flex;
  justify-content: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};
  transform: ${({ $offsetX, $offsetY, $tilt }) =>
    `translate(${String($offsetX)}px, ${String($offsetY)}px) rotate(${String($tilt)}deg)`};
  padding-bottom: ${({ $hasConnector }) => ($hasConnector ? '18px' : '0')};
  overflow: visible;

  @media (max-width: 900px) {
    justify-content: flex-start;
    transform: none;
    padding-bottom: ${({ $hasConnector }) => ($hasConnector ? '42px' : '0')};
  }
`;

const HowItWorksStepCluster = styled.div`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: flex-start;
  gap: 0.55rem;
`;

const HowItWorksStepBadge = styled.span`
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #e0a15e;
  color: #e0a15e;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1;
`;

const HowItWorksStepText = styled.p`
  margin: 0;
  border-radius: 10px;
  padding: 2.58rem 0.64rem;
  background: transparent url('/images/postItNote.png') center / cover no-repeat;
  color: #1a1a1a;
  line-height: 1.32;
  font-size: clamp(0.83rem, 0.95vw, 0.9rem);
  width: clamp(176px, 22vw, 216px);
  aspect-ratio: 1 / 1;
  overflow-wrap: anywhere;
  overflow: hidden;
  text-wrap: pretty;
`;

const HowItWorksConnector = styled.img<{
  $align: 'left' | 'right';
  $tilt: number;
  $offset: number;
  $scale: number;
  $top: number;
  $maxWidth: number;
}>`
  position: absolute;
  z-index: 1;
  pointer-events: none;
  width: ${({ $maxWidth }) => `clamp(148px, 22vw, ${String($maxWidth)}px)`};
  top: ${({ $top }) => `${String($top)}%`};
  ${({ $align, $offset }) => ($align === 'right' ? `right: ${String($offset)}px;` : `left: ${String($offset)}px;`)}
  transform: ${({ $tilt, $scale }) =>
    `translateY(-50%) rotate(${String($tilt)}deg) scale(${String($scale)})`};
  transform-origin: center;
  opacity: 0.9;

  @media (max-width: 900px) {
    width: clamp(124px, 36vw, 164px);
    left: 70px;
    right: auto;
    top: 72%;
    transform: translateY(-50%) rotate(10deg) scale(0.95);
  }
`;

const BestFitPanel = styled.section`
  border: 3px dashed rgba(224, 161, 94, 0.72);
  border-radius: 16px;
  padding: 1rem;
  display: grid;
  gap: 0.85rem;
  background: transparent;
  width: min(980px, 100%);
  margin-inline: auto;
  margin-block: clamp(2.5rem, 6vw, 4.5rem);
`;

const BestFitTitle = styled.h3`
  margin: 0;
  text-align: center;
  font-size: clamp(1.05rem, 1.85vw, 1.32rem);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.gray900};
`;

const BestFitList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.8rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const BestFitItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.48rem;
  align-items: start;
  color: ${({ theme }) => theme.colors.gray700};
  line-height: 1.38;
`;



const SummaryList = styled.ul`
  margin: 0;
  padding-left: 1rem;
  display: grid;
  gap: 0.2rem;
  color: ${({ theme }) => theme.colors.gray700};
`;

const ProfileFocusWrap = styled.div`
  width: 100%;
  display: grid;
  gap: 0.5rem;
  justify-items: center;
  text-align: center;
  padding: 0 0.15rem 0.05rem;
`;

const ProfileFocusStage = styled.div`
  min-height: 2.45rem;
  display: grid;
  align-items: center;
  justify-items: center;
  overflow: hidden;
`;

const ProfileFocusItem = styled.div<{ $phase: 'idle' | 'entering' | 'exiting' }>`
  margin: 0;
  color: ${({ theme }) => theme.colors.gray900};
  font-size: clamp(1.15rem, 2.7vw, 1.52rem);
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.01em;
  text-align: center;
  text-wrap: balance;

  ${({ $phase }) => {
    if ($phase === 'entering') {
      return css`
        animation: ${profileEnterFromRight} ${PROFILE_ITEM_TRANSITION_MS}ms ease forwards;
      `;
    }
    if ($phase === 'exiting') {
      return css`
        animation: ${profileExitToLeft} ${PROFILE_ITEM_TRANSITION_MS}ms ease forwards;
      `;
    }
    return css`
      animation: none;
    `;
  }}
`;

const ProfileFocusDots = styled.div`
  display: flex;
  align-items: center;
  gap: 0.44rem;
`;

const ProfileFocusDot = styled.button<{ $active: boolean }>`
  width: 9px;
  height: 9px;
  border-radius: 999px;
  border: 0;
  padding: 0;
  cursor: pointer;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : `${theme.colors.gray200}`};
`;

const PrimaryButton = styled.button`
  ${gradientPillCss};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  border: 0;
`;

const ActionStack = styled.div`
  display: grid;
  gap: 0.42rem;

  > * {
    width: 100%;
  }
`;

const CompactActionRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
`;

const ShareButton = styled.button`
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: #e0a15e;
  border-radius: 10px;
  padding: 0.5rem 0.72rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.85);
    background: rgba(224, 161, 94, 0.08);
  }
`;

const ClearCartButton = styled(ShareButton)`
  border-color: rgba(255, 255, 255, 0.18);
  color: ${({ theme }) => theme.colors.gray900};

  &:hover {
    border-color: rgba(255, 255, 255, 0.26);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const RightColumn = styled.aside`
  display: grid;
  gap: 0.75rem;
  margin-left: 0.75rem;
  align-self: start;
  position: sticky;
  top: 88px;
  height: fit-content;

  @media (max-width: 1080px) {
    display: none;
  }
`;

const SidePanelTitle = styled.h3`
  margin: 0;
  ${playfulTitleTilt};
`;

const SidePanel = styled.aside`
  border: none;
  border-radius: 18px;
  background: transparent;
  padding: 1rem;
  display: grid;
  gap: 0.75rem;
`;

const OtherServicesRailCard = styled.section`
  border: none;
  border-radius: 18px;
  background: transparent;
  padding: 0.85rem;
  display: grid;
  gap: 0.65rem;
`;

const RailSectionTitle = styled.strong`
  ${playfulTitleTilt};
`;

const OtherServicesRailList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0.5rem;
  min-height: 100%;
`;

const OtherServiceCardLink = styled(Link)<{ $variant: 'platform' | 'marketing' }>`
  text-decoration: none;
  border: none;
  border-radius: 14px;
  color: #ffffff;
  padding: 0.7rem;
  display: grid;
  gap: 0.4rem;
  align-content: end;
   width: auto;
  height: 100%;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.24);
  background: ${({ $variant }) =>
    $variant === 'platform'
      ? 'linear-gradient(140deg, #1f3160 0%, #1a7fbe 48%, #69d2ff 100%)'
      : 'linear-gradient(140deg, #6f1d8f 0%, #ff2e8a 52%, #ff7a18 100%)'};
`;

const OtherServiceCardTitle = styled.strong`
  font-size: 0.92rem;
  line-height: 1.2;
`;

const OtherServiceCardTeaser = styled.span`
  font-size: 0.78rem;
  line-height: 1.25;
  color: rgba(255, 255, 255, 0.92);
`;

const UpsellShowcaseSection = styled.section`
  position: relative;
  isolation: isolate;
  overflow: clip;
  margin-top: 1.25rem;
  min-height: 52svh;
  width: 100%;
  max-width: 100%;
  margin-bottom: -2rem;
  background: #efecf0;
  color: #0f172a;
  padding: clamp(1.1rem, 2.2vw, 2rem) clamp(1rem, 2.8vw, 2rem);
  display: flex;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(31, 41, 55, 0.8) 1px, transparent 1px),
      linear-gradient(90deg, rgba(31, 41, 55, 0.8) 1px, transparent 1px);
    background-size: 54px 54px;
    opacity: 0.22;
    pointer-events: none;
    z-index: 0;
    mask-image: radial-gradient(circle at 62% 46%, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.72) 38%, rgba(0, 0, 0, 0.24) 72%, transparent 100%);
  }
`;

const UpsellShowcaseInner = styled.div`
  position: relative;
  z-index: 1;
  width: min(1320px, 100%);
  margin: 0 auto;
  display: grid;
  gap: clamp(1rem, 2.2vw, 2rem);
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: stretch;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const UpsellShowcaseCopy = styled.article`
  border-radius: 18px;
  background: transparent;
  border: none;
  padding: clamp(1rem, 2vw, 1.4rem);
  display: grid;
  gap: 0.9rem;
  align-content: center;
`;

const UpsellShowcaseTitle = styled.h3`
  margin: 0;
  font-size: clamp(1.35rem, 2.6vw, 2.1rem);
  line-height: 1.08;
  color: #0f172a;
  ${playfulTitleTilt};
`;

const UpsellShowcaseLead = styled.p`
  margin: 0;
  color: rgba(15, 23, 42, 0.76);
  line-height: 1.45;
`;

const UpsellShowcaseCards = styled.div`
  display: grid;
  gap: 0.7rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const UpsellShowcaseCard = styled.article`
  border-radius: 14px;
  border: none;
  background: transparent;
  padding: 0.75rem 0.8rem;
  display: grid;
  gap: 0.45rem;
`;

const UpsellShowcaseActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const UpsellCtaPrimary = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  ${gradientPillCss};
`;

const UpsellShowcaseStackWrap = styled.aside`
  position: relative;
  isolation: isolate;
  border-radius: 18px;
  background: transparent;
  border: none;
  width: 100%;
  min-height: 100%;
  padding: clamp(0.8rem, 1.8vw, 1.2rem);
  display: grid;
  align-items: center;
  justify-items: center;
`;

const UpsellShowcaseImage = styled.img`
  position: relative;
  z-index: 1;
  display: block;
  width: min(100%, 720px);
  max-width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 26px 38px rgba(15, 23, 42, 0.18));
  transform-origin: 50% 58%;
  animation: ${blimpGlide} 10s ease-in-out infinite;

  @media (max-width: 1080px) {
    width: min(100%, 560px);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

function getTierMeta(tier: PriceBookSku): Record<string, unknown> {
  return tier.metadata as Record<string, unknown>;
}

function getTierMetaValue(tier: PriceBookSku, key: string): string {
  const value = getTierMeta(tier)[key];
  if (typeof value === 'string' && value.trim()) {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return 'Not specified';
}

type AssetsDesignServicePageProps = {
  readonly service: ServiceDefinition;
};

export default function AssetsDesignServicePage({ service }: AssetsDesignServicePageProps) {
  usePricebookCatalogSync();
  const location = useLocation();
  const navigate = useNavigate();
  const { items, replaceItems, clearCart } = useFunnelCart();
  const currentService = getServiceBySlug(service.slug) ?? service;
  const tiers = useMemo(() => [...currentService.tiers], [currentService.tiers]);
  const currentServiceTierSkuIds = useMemo(
    () => new Set(currentService.tiers.map((tier) => tier.skuId)),
    [currentService.tiers],
  );
  const currentServiceSkuIds = useMemo(
    () => new Set([...currentService.tiers, ...currentService.addOns].map((sku) => sku.skuId)),
    [currentService.addOns, currentService.tiers],
  );

  const cartSelectedTierSkuId =
    items.find((item) => currentServiceTierSkuIds.has(item.skuId))?.skuId ?? null;
  const defaultTierSkuId = tiers[0]?.skuId ?? '';
  const assetsAddOnCartItems = useMemo(
    () =>
      items.filter(
        (item) =>
          currentServiceSkuIds.has(item.skuId) && !currentServiceTierSkuIds.has(item.skuId),
      ),
    [currentServiceSkuIds, currentServiceTierSkuIds, items],
  );
  const serviceAddOnSkuIds = useMemo(
    () => new Set(currentService.addOns.map((addOn) => addOn.skuId)),
    [currentService.addOns],
  );
  const cartSelectedOptionalAddOnSkuIds = useMemo(
    () => assetsAddOnCartItems.map((item) => item.skuId),
    [assetsAddOnCartItems],
  );
  const sharedConfigToken = useMemo(
    () => new URLSearchParams(location.search).get('config') ?? '',
    [location.search],
  );
  const parsedSharedConfig = useMemo(
    () => (sharedConfigToken ? parseSharedServiceConfigToken(sharedConfigToken) : null),
    [sharedConfigToken],
  );
  const validSharedConfig = useMemo(() => {
    if (!parsedSharedConfig || parsedSharedConfig.servicePath !== '/services/assets-design') {
      return null;
    }

    const tierToShare = tiers.find((tier) => tier.skuId === parsedSharedConfig.tierSkuId);
    if (!tierToShare) return null;

    return {
      ...parsedSharedConfig,
      addOnSkuIds: (parsedSharedConfig.addOnSkuIds ?? []).filter((skuId) =>
        serviceAddOnSkuIds.has(skuId),
      ),
    };
  }, [parsedSharedConfig, serviceAddOnSkuIds, tiers]);
  const [focusedTierSkuId, setFocusedTierSkuId] = useState<string>(
    validSharedConfig?.tierSkuId || cartSelectedTierSkuId || defaultTierSkuId,
  );
  const [selectedOptionalAddOnSkuIds, setSelectedOptionalAddOnSkuIds] = useState<string[]>(
    validSharedConfig?.addOnSkuIds
      ? [...validSharedConfig.addOnSkuIds]
      : [...cartSelectedOptionalAddOnSkuIds],
  );
  const [activeProfileIndex, setActiveProfileIndex] = useState(0);
  const [profilePhase, setProfilePhase] = useState<'idle' | 'entering' | 'exiting'>('idle');
  const [pendingProfileIndex, setPendingProfileIndex] = useState<number | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const appliedSharedConfigTokenRef = useRef('');

  const cartSelectedTier = tiers.find((tier) => tier.skuId === cartSelectedTierSkuId) || null;
  const selectedTier = tiers.find((tier) => tier.skuId === focusedTierSkuId) || null;
  const baseTier = tiers[0] || null;
  const workingTier = selectedTier || baseTier;

  const membershipProfileItems = useMemo(
    () =>
      workingTier
        ? [
            `Term: ${getTierMetaValue(workingTier, 'contractTerm')}`,
            `Throughput: ${getTierMetaValue(workingTier, 'throughput')}`,
            `Triage SLA: ${getTierMetaValue(workingTier, 'triageSla')}`,
            `Communication: ${getTierMetaValue(workingTier, 'communication')}`,
          ]
        : [],
    [workingTier],
  );

  const queueProfileIndex = useCallback(
    (nextIndex: number) => {
      if (!membershipProfileItems.length) return;
      if (nextIndex === activeProfileIndex) return;

      if (profilePhase !== 'idle') {
        setActiveProfileIndex(nextIndex);
        setPendingProfileIndex(null);
        setProfilePhase('entering');
        return;
      }

      setPendingProfileIndex(nextIndex);
      setProfilePhase('exiting');
    },
    [activeProfileIndex, membershipProfileItems.length, profilePhase],
  );

  useEffect(() => {
    setActiveProfileIndex(0);
    setPendingProfileIndex(null);
    setProfilePhase('entering');
  }, [membershipProfileItems.length]);

  useEffect(() => {
    if (membershipProfileItems.length <= 1) return;
    const timer = window.setInterval(() => {
      if (profilePhase !== 'idle') return;
      queueProfileIndex((activeProfileIndex + 1) % membershipProfileItems.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, [activeProfileIndex, membershipProfileItems.length, profilePhase, queueProfileIndex]);

  useEffect(() => {
    if (profilePhase === 'exiting' && pendingProfileIndex !== null) {
      const timeout = window.setTimeout(() => {
        setActiveProfileIndex(pendingProfileIndex);
        setPendingProfileIndex(null);
        setProfilePhase('entering');
      }, PROFILE_ITEM_TRANSITION_MS);
      return () => window.clearTimeout(timeout);
    }

    if (profilePhase === 'entering') {
      const timeout = window.setTimeout(() => {
        setProfilePhase('idle');
      }, PROFILE_ITEM_TRANSITION_MS);
      return () => window.clearTimeout(timeout);
    }
  }, [pendingProfileIndex, profilePhase]);

  useEffect(() => {
    if (
      sharedConfigToken &&
      validSharedConfig &&
      appliedSharedConfigTokenRef.current !== sharedConfigToken
    ) {
      setFocusedTierSkuId(validSharedConfig.tierSkuId);
      setSelectedOptionalAddOnSkuIds([...(validSharedConfig.addOnSkuIds ?? [])]);
      appliedSharedConfigTokenRef.current = sharedConfigToken;
      return;
    }

    const hasFocusedTier = tiers.some((tier) => tier.skuId === focusedTierSkuId);
    if (!hasFocusedTier && defaultTierSkuId) {
      setFocusedTierSkuId(defaultTierSkuId);
    }
  }, [defaultTierSkuId, focusedTierSkuId, sharedConfigToken, tiers, validSharedConfig]);

  const commitCurrentSelectionToCart = useCallback(() => {
    if (!workingTier) return false;

    const nextItems = items.filter((item) => !currentServiceSkuIds.has(item.skuId));

    replaceItems([
      ...nextItems,
      { skuId: workingTier.skuId, quantity: 1 },
      ...selectedOptionalAddOnSkuIds.map((skuId) => ({ skuId, quantity: 1 })),
    ]);
    return true;
  }, [currentServiceSkuIds, items, replaceItems, selectedOptionalAddOnSkuIds, workingTier]);

  const handleAddToCart = () => {
    commitCurrentSelectionToCart();
  };

  const handleOpenShareModal = () => {
    if (!workingTier) return;
    const nextShareUrl = buildSharedServiceConfigUrl({
      servicePath: '/services/assets-design',
      tierSkuId: workingTier.skuId,
      addOnSkuIds: selectedOptionalAddOnSkuIds,
      createdAt: Date.now(),
    });
    setShareUrl(nextShareUrl);
    setShareModalOpen(true);
  };

  const ensureSelectedTierThenGo = (path: string) => {
    if (!commitCurrentSelectionToCart()) return;
    navigate(path);
  };

  const isWorkingTierInCart =
    Boolean(workingTier) &&
    cartSelectedTier?.skuId === workingTier?.skuId &&
    haveSameIds(cartSelectedOptionalAddOnSkuIds, selectedOptionalAddOnSkuIds);

  const handleSnapshotPrimaryAction = () => {
    if (isWorkingTierInCart) {
      ensureSelectedTierThenGo('/checkout');
      return;
    }
    handleAddToCart();
  };

  return (
    <>
      <FunnelShell>
      <Hero>
        <HeroTitle>{currentService.title}</HeroTitle>
      </Hero>

      <Layout>
        <Main>
          <FirstSectionBlock>
            <FirstSectionHeader>
              <FirstSectionTitle>Join CyBard Club</FirstSectionTitle>
              <FirstSectionLead>
                Single membership model.
                <br />
                Add it to your cart when you are ready.
              </FirstSectionLead>
            </FirstSectionHeader>

            <FirstSectionShowcase>
              {workingTier ? (
                <PlanShowcase>
                  <JoinCard>
                    <GiftCardWrapper>
                      <GiftCard />
                    </GiftCardWrapper>
                    <JoinContent>
                      <JoinButton type="button" onClick={handleAddToCart}>
                        <BlinkDot />
                        Start today
                      </JoinButton>
                      <JoinHeading>
                        Join
                        <br />
                        CyBard
                      </JoinHeading>
                      <JoinSubtext>{workingTier.name}</JoinSubtext>
                    </JoinContent>
                  </JoinCard>
                </PlanShowcase>
              ) : null}

              {workingTier && membershipProfileItems.length > 0 ? (
                <ProfileFocusWrap>
                  <ProfileFocusStage aria-live="polite">
                    <ProfileFocusItem
                      key={`${activeProfileIndex}-${membershipProfileItems[activeProfileIndex]}`}
                      $phase={profilePhase}
                    >
                      {membershipProfileItems[activeProfileIndex]}
                    </ProfileFocusItem>
                  </ProfileFocusStage>
                  <ProfileFocusDots aria-label="Membership profile highlights">
                    {membershipProfileItems.map((item, index) => (
                      <ProfileFocusDot
                        key={item}
                        type="button"
                        $active={index === activeProfileIndex}
                        aria-label={`Show highlight ${index + 1}`}
                        onClick={() => queueProfileIndex(index)}
                      />
                    ))}
                  </ProfileFocusDots>
                </ProfileFocusWrap>
              ) : null}
            </FirstSectionShowcase>
          </FirstSectionBlock>

          <BestFitPanel>
            <BestFitTitle>Best fit for this membership</BestFitTitle>
            <BestFitList>
              {BEST_FIT.map((item) => (
                <BestFitItem key={item}>
                  <span>{item}</span>
                </BestFitItem>
              ))}
            </BestFitList>
          </BestFitPanel>

          <SectionBlock>
            <FirstSectionHeader>
              <HowItWorksSectionTitle>How It Works</HowItWorksSectionTitle>
              <SectionLead>
                Clear handoff expectations with a predictable async workflow.
              </SectionLead>
            </FirstSectionHeader>
            <HowItWorksLayout>
              <div style={{ display: 'grid', gap: '0.7rem' }}>
                <HowItWorksFlowTitle>Delivery flow</HowItWorksFlowTitle>
                <HowItWorksFlow>
                  {HOW_IT_WORKS.map((item, index) => {
                    const align = index % 2 === 0 ? 'left' : 'right';
                    const hasConnector = index < HOW_IT_WORKS.length - 1;

                    return (
                      <HowItWorksStep
                        key={item}
                        $align={align}
                        $offsetX={HOW_IT_WORKS_X_OFFSETS[index % HOW_IT_WORKS_X_OFFSETS.length] ?? 0}
                        $offsetY={HOW_IT_WORKS_Y_OFFSETS[index % HOW_IT_WORKS_Y_OFFSETS.length] ?? 0}
                        $tilt={HOW_IT_WORKS_TILTS[index % HOW_IT_WORKS_TILTS.length] ?? 0}
                        $hasConnector={hasConnector}
                      >
                        <HowItWorksStepCluster>
                          <HowItWorksStepBadge>{index + 1}</HowItWorksStepBadge>
                          <HowItWorksStepText>{item}</HowItWorksStepText>
                        </HowItWorksStepCluster>
                        {hasConnector ? (
                          <HowItWorksConnector
                            src={`/images/squiggle${(index % 3) + 1}.png`}
                            alt=""
                            aria-hidden
                            $align={align}
                            $tilt={
                              HOW_IT_WORKS_CONNECTOR_TILTS[
                                index % HOW_IT_WORKS_CONNECTOR_TILTS.length
                              ] ?? 10
                            }
                            $offset={
                              HOW_IT_WORKS_CONNECTOR_OFFSETS[
                                index % HOW_IT_WORKS_CONNECTOR_OFFSETS.length
                              ] ?? 64
                            }
                            $scale={
                              HOW_IT_WORKS_CONNECTOR_SCALES[
                                index % HOW_IT_WORKS_CONNECTOR_SCALES.length
                              ] ?? 1
                            }
                            $top={
                              HOW_IT_WORKS_CONNECTOR_TOPS[
                                index % HOW_IT_WORKS_CONNECTOR_TOPS.length
                              ] ?? 66
                            }
                            $maxWidth={
                              HOW_IT_WORKS_CONNECTOR_MAX_WIDTHS[
                                index % HOW_IT_WORKS_CONNECTOR_MAX_WIDTHS.length
                              ] ?? 196
                            }
                          />
                        ) : null}
                      </HowItWorksStep>
                    );
                  })}
                </HowItWorksFlow>
              </div>
            </HowItWorksLayout>
          </SectionBlock>
          <SectionBlock>
            <SectionTitle>Rules And Boundaries</SectionTitle>
            <RulesMiniGrid>
              <MiniCard>
                <RuleContentRow $imageSide="right">
                  <RuleImageColumn>
                    <RuleMascot src="/images/drakeYup.png" alt="Included reaction" />
                  </RuleImageColumn>
                  <RuleTextColumn>
                    <MiniCardIcon src="/images/checkMark.png" alt="Included symbol" />
                    <strong>Included</strong>
                    <RuleList>
                      {INCLUDED_BY_DEFAULT.map((item) => (
                        <RuleItem key={item}>
                          <RuleGlyph $kind="include">&#10003;</RuleGlyph>
                          <span>{item}</span>
                        </RuleItem>
                      ))}
                    </RuleList>
                  </RuleTextColumn>
                </RuleContentRow>
              </MiniCard>
              <MiniCard>
                <RuleContentRow $imageSide="left">
                  <RuleTextColumn $align="right">
                    <MiniCardIcon src="/images/xOut.png" alt="Not included symbol" />
                    <strong>Not included</strong>
                    <RuleList style={{ alignItems: 'flex-end' }}>
                      {NOT_INCLUDED.map((item) => (
                        <RuleItem key={item}>
                          <RuleGlyph $kind="exclude">&#10005;</RuleGlyph>
                          <span>{item}</span>
                        </RuleItem>
                      ))}
                    </RuleList>
                  </RuleTextColumn>
                  <RuleImageColumn>
                    <RuleMascot src="/images/drakeNope.png" alt="Not included reaction" />
                  </RuleImageColumn>
                </RuleContentRow>
              </MiniCard>
            </RulesMiniGrid>
          </SectionBlock>

          <CompactSectionBlock>
            <SectionTitle>Ready To Buy?</SectionTitle>
            <SectionLead>
              Add this membership to cart, then continue to checkout.
            </SectionLead>
            <CompactActionRow>
              <PrimaryButton type="button" onClick={handleAddToCart}>
                Add to cart
              </PrimaryButton>
              <ShareButton type="button" onClick={handleOpenShareModal}>
                Share configuration
              </ShareButton>
            </CompactActionRow>
          </CompactSectionBlock>
        </Main>

        <RightColumn>
          <SidePanel>
            <SidePanelTitle>Selection Snapshot</SidePanelTitle>
            <p style={{ margin: 0, fontWeight: 700 }}>
              {workingTier ? workingTier.name : 'No plan available'}
            </p>
            {workingTier ? (
              <SummaryList>
                <li>Term: {getTierMetaValue(workingTier, 'contractTerm')}</li>
                <li>Throughput: {getTierMetaValue(workingTier, 'throughput')}</li>
                <li>Triage SLA: {getTierMetaValue(workingTier, 'triageSla')}</li>
                <li>Account manager: Not included</li>
                <li>Project manager: Not included</li>
              </SummaryList>
            ) : (
              <p style={{ margin: 0 }}>No plan configured.</p>
            )}
            <p style={{ margin: 0 }}>
              CyBard Club is an async ticket membership. For project-based work, use the
              alternative service tracks.
            </p>
            <ActionStack>
              <PrimaryButton type="button" onClick={handleSnapshotPrimaryAction}>
                {isWorkingTierInCart ? 'Proceed to checkout' : 'Add to cart'}
              </PrimaryButton>
              <ShareButton type="button" onClick={handleOpenShareModal}>
                Share package
              </ShareButton>
            </ActionStack>
          </SidePanel>

          <OtherServicesRailCard>
            <RailSectionTitle>View Other Services</RailSectionTitle>
            <OtherServicesRailList>
              <OtherServiceCardLink $variant="platform" to="/services/platform-development">
                <OtherServiceCardTitle>Platform Development</OtherServiceCardTitle>
                <OtherServiceCardTeaser>
                  Product roadmap, integrations, and full engineering delivery.
                </OtherServiceCardTeaser>
              </OtherServiceCardLink>
              <OtherServiceCardLink $variant="marketing" to="/services/managed-marketing">
                <OtherServiceCardTitle>Managed Marketing</OtherServiceCardTitle>
                <OtherServiceCardTeaser>
                  Retainer growth ops for SEO, paid media, lifecycle, and performance.
                </OtherServiceCardTeaser>
              </OtherServiceCardLink>
            </OtherServicesRailList>
          </OtherServicesRailCard>
        </RightColumn>

        <MobileSnapshotFab title="Selection Snapshot" buttonLabel="Snapshot">
          <SidePanel>
            <SidePanelTitle>Selection Snapshot</SidePanelTitle>
            <p style={{ margin: 0, fontWeight: 700 }}>
              {workingTier ? workingTier.name : 'No plan available'}
            </p>
            {workingTier ? (
              <SummaryList>
                <li>Term: {getTierMetaValue(workingTier, 'contractTerm')}</li>
                <li>Throughput: {getTierMetaValue(workingTier, 'throughput')}</li>
                <li>Triage SLA: {getTierMetaValue(workingTier, 'triageSla')}</li>
                <li>Account manager: Not included</li>
                <li>Project manager: Not included</li>
              </SummaryList>
            ) : (
              <p style={{ margin: 0 }}>No plan configured.</p>
            )}
            <p style={{ margin: 0 }}>
              CyBard Club is an async ticket membership. For project-based work, use the
              alternative service tracks.
            </p>
            <ActionStack>
              <PrimaryButton type="button" onClick={handleSnapshotPrimaryAction}>
                {isWorkingTierInCart ? 'Proceed to checkout' : 'Add to cart'}
              </PrimaryButton>
              <ClearCartButton type="button" onClick={clearCart}>
                Clear cart
              </ClearCartButton>
              <ShareButton type="button" onClick={handleOpenShareModal}>
                Share package
              </ShareButton>
            </ActionStack>
          </SidePanel>

          <OtherServicesRailCard>
            <RailSectionTitle>View Other Services</RailSectionTitle>
            <OtherServicesRailList>
              <OtherServiceCardLink $variant="platform" to="/services/platform-development">
                <OtherServiceCardTitle>Platform Development</OtherServiceCardTitle>
                <OtherServiceCardTeaser>
                  Product roadmap, integrations, and full engineering delivery.
                </OtherServiceCardTeaser>
              </OtherServiceCardLink>
              <OtherServiceCardLink $variant="marketing" to="/services/managed-marketing">
                <OtherServiceCardTitle>Managed Marketing</OtherServiceCardTitle>
                <OtherServiceCardTeaser>
                  Retainer growth ops for SEO, paid media, lifecycle, and performance.
                </OtherServiceCardTeaser>
              </OtherServiceCardLink>
            </OtherServicesRailList>
          </OtherServicesRailCard>
        </MobileSnapshotFab>
      </Layout>

      <ShareCheckoutLinkModal
        open={shareModalOpen}
        shareUrl={shareUrl}
        onClose={() => setShareModalOpen(false)}
      />
      </FunnelShell>
      <UpsellShowcaseSection>
        <UpsellShowcaseInner>
          <UpsellShowcaseCopy>
            <UpsellShowcaseTitle>Need More Than a Standard Tier?</UpsellShowcaseTitle>
            <UpsellShowcaseLead>
              Custom scopes and multi-service builds are handled directly with our team. If your
              requirements are outside the standard package flow, start a consult and we will map
              the right delivery model.
            </UpsellShowcaseLead>
            <UpsellShowcaseCards>
              <UpsellShowcaseCard>
                <UpsellShowcaseActions>
                  <UpsellCtaPrimary to="/services/custom-projects">
                    Consult with our team
                  </UpsellCtaPrimary>
                </UpsellShowcaseActions>
              </UpsellShowcaseCard>
            </UpsellShowcaseCards>
          </UpsellShowcaseCopy>

          <UpsellShowcaseStackWrap>
            <UpsellShowcaseImage src="/images/CybardBlimp.png" alt="CyBard blimp" />
          </UpsellShowcaseStackWrap>
        </UpsellShowcaseInner>
      </UpsellShowcaseSection>
    </>
  );
}
